import { useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message, StreamingMessage } from '../types';
import { MessageBubble } from './MessageBubble';

interface MessageListProps {
  messages: Message[];
  isTyping: boolean;
  onTabChange?: (tab: 'you' | 'files') => void;
  streamingMessage?: StreamingMessage | null;
}

export function MessageList({ messages, isTyping, onTabChange, streamingMessage }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, streamingMessage?.content]);

  return (
    <div className="flex-1 overflow-y-auto p-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} onTabChange={onTabChange} />
      ))}

      {/* Streaming assistant response */}
      {streamingMessage && streamingMessage.content && (
        <div className="flex justify-start mb-4">
          <div className="max-w-[80%] rounded-lg px-4 py-2 bg-gray-100 text-gray-900">
            <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-2 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-strong:text-gray-900">
              <Markdown remarkPlugins={[remarkGfm]}>{streamingMessage.content}</Markdown>
            </div>
          </div>
        </div>
      )}

      {/* Typing indicator (shown when waiting for stream to start) */}
      {isTyping && !streamingMessage?.content && (
        <div
          data-testid="typing-indicator"
          className="flex justify-start mb-4"
        >
          <div className="bg-gray-100 rounded-lg px-4 py-2">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
